import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import {
  AviaSearchAppliedFilter,
  AviaSearchFilterItem,
  AviaSearchFilterPrice,
  AviaSearchFilterValue,
  AviaSearchPriceSelected,
  AviaSearchTimeSelected
} from '../../../../services/router/interfaces/avia-search';
import { WindowRefService } from '../../../../services/window-ref.service';

@Component({
  selector: 'app-filter-pill',
  templateUrl: './filter-pill.component.html',
  styleUrls: ['./filter-pill.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class FilterPillComponent {
  @Input() filterType = '';
  @Input() filterName = '';
  @Input() appliedLabel = '';
  @Input() filter: AviaSearchFilterItem | null = null;
  @Input() appliedFilter: AviaSearchAppliedFilter | null = null;
  @Output() openSheet = new EventEmitter<string>();
  @Output() resetFilter = new EventEmitter<string>();

  constructor(
    private window: WindowRefService,
  ) {
  }

  get isApply(): boolean {
    return Boolean(this.appliedFilter);
  }

  get filterPillLabel(): string {
    if (this.appliedFilter && this.appliedLabel) {
      return this.appliedLabel;
    }

    if (this.filterType === 'sortBy') {
      const selectedSortBy = this.appliedFilter?.selected as string || this.filter?.selected as string || 'price';

      return this.getFilterValueLabel(selectedSortBy);
    }

    if (this.filterType === 'price' && this.appliedFilter) {
      const selected = this.appliedFilter.selected as AviaSearchPriceSelected;
      const from = this.formatPrice(Number(selected.from), this.filter?.min);
      const to = this.formatPrice(Number(selected.to), this.filter?.max);

      return `от ${from} до ${to}`;
    }

    if (this.filterType === 'airlines' && this.appliedFilter) {
      const selectedAirlines = this.getSelectedAirlines();
      if (!selectedAirlines.length) {
        return this.filterName || this.filter?.name || this.filterType;
      }
      const firstAirline = selectedAirlines[0];
      const firstAirlineLabel = this.getAirlineLabel(firstAirline);

      if (selectedAirlines.length === 1) {
        return firstAirlineLabel;
      }

      return `${firstAirlineLabel} +${selectedAirlines.length - 1}`;
    }

    if (this.filterType === 'baggageOnly' && this.appliedFilter) {
      return this.filterName || this.filter?.name || this.filterType;
    }

    if (this.isTimeRangeFilter && this.appliedFilter) {
      const selected = this.appliedFilter.selected as AviaSearchTimeSelected;
      return `${selected.from} - ${selected.to}`;
    }

    if (this.isScalarSelectedFilter && this.appliedFilter) {
      const selectedValue = this.appliedFilter.selected as string;
      const selectedLabel = this.getFilterValueLabel(selectedValue);

      return selectedLabel === selectedValue
        ? this.filterName || this.filter?.name || selectedLabel
        : selectedLabel;
    }

    return this.filterName || this.filter?.name || this.filterType;
  }

  showFilterSheet() {
    this.openSheet.emit(this.filterType);

    this.window.nativeWindow.brxTracking?.sendAnalyticsEvent?.('click_filter_button', {
      'Filter Type': this.filterType,
    });
  }

  clearFilter(event: Event) {
    event.stopPropagation();
    this.resetFilter.emit(this.filterType);
  }

  private formatPrice(value: number, price: AviaSearchFilterPrice | undefined): string {
    const formattedValue = String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const currencySign = price?.currencySign || price?.currency_sign || price?.currency || '';

    return currencySign ? `${formattedValue} ${currencySign}` : formattedValue;
  }

  private getSelectedAirlines(): string[] {
    return Array.isArray(this.appliedFilter?.selected)
      ? this.appliedFilter.selected
      : [];
  }

  private getAirlineLabel(airlineCode: string): string {
    return this.getFilterValueLabel(airlineCode);
  }

  private get isScalarSelectedFilter(): boolean {
    return [
      'flightType',
      'stops',
      'stopoverDuration',
      'withoutNightStopovers',
      'baggageOnly',
      'sortBy',
    ].includes(this.filterType);
  }

  private get isTimeRangeFilter(): boolean {
    return [
      'departureTimeFromOrigin',
      'departureTimeFromDestination',
    ].includes(this.filterType);
  }

  private getFilterValueLabel(filterValue: string): string {
    const option = (this.filter?.values || []).find((value) => {
      if (typeof value === 'string') {
        return value === filterValue;
      }

      return value.filterValue === filterValue;
    }) as AviaSearchFilterValue | string | undefined;

    if (!option) {
      return filterValue;
    }

    return typeof option === 'string'
      ? option
      : option.viewValue || option.filterValue || filterValue;
  }
}
